/* eslint-disable  */
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { bindActionCreators } from "redux";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import {
  Package,
  ShoppingBag,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  ChevronRight,
  MoreHorizontal,
  LayoutDashboard,
  ClipboardList,
} from "lucide-react";

// Components & Actions
import Loader from "../Components/Loader";
import RenderHirePurchasePage from "./RenderHirePurchasePage";
import { GetUserProfile } from "../services/Actions/authActions";
import { productActionCreators } from "../services/Actions";

function HirePurchaseDashboard() {
  const history = useHistory();
  const dispatch = useDispatch();

  const { GetSubmittedOrders, GetTotals, ResetProductResponse } =
    bindActionCreators(productActionCreators, dispatch);

  const product = useSelector((state) => state.product);
  const { isLoading, orders, totals, response } = product;

  const {
    totalProducts,
    totalOrders,
    totalApprovedOrders,
    totalInitiatedOrders,
    totalRejectedOrders,
    totalShippedOrders,
    totalSubmitedOrders,
  } = totals;

  useEffect(() => {
    dispatch(GetUserProfile());
    GetSubmittedOrders(true, 1, 5, "");
    GetTotals();
  }, []);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      setTimeout(() => ResetProductResponse(), 1500);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetProductResponse(), 1500);
    }
  }, [response?.state]);

  return (
    <RenderHirePurchasePage title="Mart Overview">
      {isLoading && <Loader />}

      {/* STATS GRID */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <DashboardCard
          title="Total Products"
          value={totalProducts}
          icon={<Package size={24} />}
          link="/mart/product"
          color="bg-blue-600"
        />
        <DashboardCard
          title="Total Orders"
          value={totalOrders}
          icon={<ShoppingBag size={24} />}
          link="/mart/status-order"
          color="bg-indigo-600"
        />
        <DashboardCard
          title="New Orders"
          value={totalSubmitedOrders}
          icon={<ClipboardList size={24} />}
          link="/mart/order"
          color="bg-emerald-600"
        />
        <DashboardCard
          title="Initiated"
          value={totalInitiatedOrders}
          icon={<Clock size={24} />}
          link="/mart/processed-order"
          color="bg-amber-500"
        />
        <DashboardCard
          title="Approved"
          value={totalApprovedOrders}
          icon={<CheckCircle size={24} />}
          link="/mart/approved-order"
          color="bg-teal-600"
        />
        <DashboardCard
          title="Shipped"
          value={totalShippedOrders}
          icon={<Truck size={24} />}
          link="/mart/shipped-order"
          color="bg-purple-600"
        />
        <DashboardCard
          title="Rejected"
          value={totalRejectedOrders}
          icon={<XCircle size={24} />}
          link="/mart/rejected-order"
          color="bg-rose-600"
        />
      </section>

      {/* RECENT ORDERS TABLE */}
      <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-100 dark:border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-slate-50 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h2 className="text-xs font-bold text-slate-800 dark:text-slate-100">
              New Order Requests
            </h2>
            {/* <p className="text-sm text-slate-500">
              Latest submissions awaiting processing
            </p> */}
          </div>
          <Link
            to="/mart/order"
            className="text-indigo-600 font-semibold text-sm hover:underline"
          >
            View All
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-gray-800">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Member Info
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Affordability
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Facility
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {orders?.map((order, index) => (
                <tr
                  key={index}
                  className="hover:bg-slate-50/80 dark:hover:bg-gray-700/80 transition-colors group"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        className="w-10 h-10 rounded-full border border-slate-200 object-cover shadow-sm"
                        src={
                          order?.user_id?.profile_image ||
                          "/images/dev/avatar1.png"
                        }
                        alt="Profile"
                      />
                      <div>
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">
                          {order?.user_id?.name}
                        </p>
                        <p className="text-xs font-medium text-slate-500">
                          {order?.user_id?.staff_id}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-2 rounded-full text-xs font-bold dark:bg-slate-700 dark:text-white bg-emerald-50 text-emerald-700 ">
                      ₵ {order?.affordability}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">
                      {order?.facility}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {moment(order?.created_at).format("MMM DD, YYYY")}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() =>
                        history.push({
                          pathname: "/mart/order-detailed",
                          state: order,
                        })
                      }
                      className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </td>
                </tr>
              ))}
              {orders?.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-12 text-center text-slate-400 italic"
                  >
                    No new orders found at the moment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <ToastContainer autoClose={3000} />
    </RenderHirePurchasePage>
  );
}

/**
 * Modern Stat Card Component
 */
const DashboardCard = ({ title, value, icon, link, color }) => (
  <div className="relative overflow-hidden bg-white p-3 rounded-2xl border border-slate-100 shadow-sm group hover:shadow-md transition-all duration-300">
    <div className="flex justify-between items-start mb-4">
      <div className={`p-3 rounded-full text-white shadow-lg ${color}`}>
        {icon}
      </div>
      <Link
        to={link}
        className="text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-indigo-600 transition-colors flex items-center gap-1"
      >
        View More <ChevronRight size={12} />
      </Link>
    </div>

    <div>
      <p className="text-sm font-medium text-slate-500">{title}</p>
      <h3 className="text-2xl font-black text-slate-800 mt-1">
        {value?.toLocaleString() || 0}
      </h3>
    </div>

    {/* Subtle Decorative Circle */}
    <div
      className={`absolute -right-4 -bottom-4 w-16 h-16 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-500 ${color}`}
    ></div>
  </div>
);

export default HirePurchaseDashboard;
